import moment from "moment";
import { calculateFormula, checkFormulaSyntax } from "services/utils/formulaUtils";

// Payroll > Configuration > Statutory > PF, on sample data until the PF
// APIs exist (PF Specification v1.3, screens P1–P3a). Values are the
// spec's example company entries, not Cauver defaults.

export const PF_BASIS_OPTIONS = [
  { value: "CEILING", label: "Restricted to ceiling" },
  { value: "ACTUAL", label: "Actual wage" },
];
export const ROUNDING_OPTIONS = [
  { value: "NEAREST", label: "Nearest rupee" },
  { value: "UP", label: "Round up" },
  { value: "DOWN", label: "Round down" },
];
export const CHALLAN_ACCOUNTS = [
  { value: "1", label: "A/c 1 · PF" },
  { value: "10", label: "A/c 10 · Pension" },
  { value: "2", label: "A/c 2 · Admin" },
  { value: "21", label: "A/c 21 · EDLI" },
  { value: "none", label: "None" },
];

export const VERSION_STATUS_COLORS = { Active: "#12B76A", Draft: "#F0954A", Superseded: "#6B7280" };
export const ACTIVE_STATUS_COLORS = { Active: "#12B76A", Inactive: "#6B7280" };

// Codes every PF version must keep (B3.1 "Required codes must exist and
// be active").
export const REQUIRED_CALCULATION_CODES = ["PF_WAGE", "EPF_WAGE", "EPS_WAGE", "EDLI_WAGE", "GROSS_WAGE"];
export const REQUIRED_CONTRIBUTION_CODES = ["EE_PF", "VPF", "ER_EPS", "ER_EPF", "EDLI", "PF_ADMIN"];

// Values a formula can read besides the PF components themselves.
export const SETTING_CODES = ["CEILING", "EPS_CAP", "EPS_STOP_AGE", "PF_BASIS", "ACTUAL", "EPS_MEMBER", "AGE"];
export const PAYROLL_CODES = ["BASIC_E", "DA_E", "RETAINING_E", "TOTAL_EARNINGS", "VPF_AMOUNT"];
// Older earned-amount codes: saved formulas may still use them, but the
// editor offers the company's salary component codes instead.
const LEGACY_CODES = ["BASIC_E", "DA_E", "RETAINING_E"];

// Salary component codes a PF formula may read (server rule): active ones,
// except statutory components (PF's own, ESI, ...).
export const salaryFormulaCodes = (salaryComponents = []) => [
  ...new Set(
    salaryComponents
      .filter((item) => item.active && item.system_type !== "Statutory" && String(item.code || "").trim())
      .map((item) => String(item.code).trim().toUpperCase()),
  ),
];

// BASIC_E -> BASIC (DA_E -> DA, ...) when the company has that salary
// component; other codes stay as they are (server: defaults.py
// use_salary_codes).
export const toSalaryCodes = (formula, salaryCodes = []) =>
  String(formula || "").replace(/\b(BASIC|DA|RETAINING)_E\b/gi, (match, code) =>
    salaryCodes.includes(code.toUpperCase()) ? code.toUpperCase() : match,
  );

// An unsaved draft (new version) with its formulas on the company's salary
// codes. Saved versions are left alone; the same form comes back when
// nothing changes.
export const draftWithSalaryCodes = (form, salaryCodes = []) => {
  if (form?.status !== "Draft" || !salaryCodes.length) return form;
  let changed = false;
  const components = form.components.map((component) => {
    const formula = toSalaryCodes(component.formula, salaryCodes);
    if (component.fixed || formula === component.formula) return component;
    changed = true;
    return { ...component, formula };
  });
  return changed ? { ...form, components } : form;
};
export const FORMULA_FUNCTIONS = ["ROUND", "ROUNDUP", "ROUNDDOWN", "MIN", "MAX", "IF"];

const calc = (code, name, formula, usedFor, extra = {}) => ({
  code,
  name,
  kind: "CALCULATION",
  formula,
  used_for: usedFor,
  challan_account: "none",
  status: "Active",
  ...extra,
});
const contrib = (code, name, formula, usedFor, account) => ({
  code,
  name,
  kind: "CONTRIBUTION",
  formula,
  used_for: usedFor,
  challan_account: account,
  status: "Active",
});

export const defaultComponents = () => [
  calc("PF_WAGE", "PF Wage", "BASIC_E + DA_E + RETAINING_E", "Earned PF wage after unpaid days"),
  calc("EPF_WAGE", "EPF Wage", "IF(PF_BASIS = ACTUAL, PF_WAGE, MIN(PF_WAGE, CEILING))", "Employee and employer PF base · ECR “EPF wages”"),
  calc("EPS_WAGE", "EPS Wage", "IF(EPS_MEMBER AND AGE < EPS_STOP_AGE, MIN(EPF_WAGE, EPS_CAP), 0)", "Pension base · ECR “EPS wages”"),
  calc("EDLI_WAGE", "EDLI Wage", "MIN(PF_WAGE, CEILING)", "Insurance base · ECR “EDLI wages”"),
  calc("GROSS_WAGE", "Gross Wage", "TOTAL_EARNINGS", "Gross earnings from finalised payroll · not editable · ECR “Gross wages”", { fixed: true }),
  contrib("EE_PF", "Employee PF", "ROUND(EPF_WAGE * 12%)", "Deduction · ECR “EE share” · A/c 1", "1"),
  contrib("VPF", "Voluntary PF", "VPF_AMOUNT", "Deduction (VPF override only) · A/c 1", "1"),
  contrib("ER_EPS", "Employer — EPS", "ROUND(EPS_WAGE * 8.33%)", "Employer cost · ECR “EPS” · A/c 10", "10"),
  contrib("ER_EPF", "Employer — EPF", "ROUND(EPF_WAGE * 12%) - ER_EPS", "Employer cost · balance of employer rate · A/c 1", "1"),
  contrib("EDLI", "EDLI", "ROUND(EDLI_WAGE * 0.5%)", "Employer charge · A/c 21", "21"),
  contrib("PF_ADMIN", "PF Admin Charges", "ROUND(EPF_WAGE * 0.5%)", "Employer charge · A/c 2", "2"),
];

// Formulas show × in the tables, like the mockup; the editor keeps *.
export const displayFormula = (component) =>
  `${String(component.formula || "").replace(/\*/g, "×")}${component.fixed ? " (fixed)" : ""}`;

const settings = {
  wage_ceiling: "25000",
  eps_cap: "25000",
  eps_stop_age: "58",
  pf_basis_default: "CEILING",
  intl_worker_enabled: false,
  post_2014_rule_enabled: false,
  vpf_enabled: true,
  vpf_max_pct: "100",
  vpf_on_actual_allowed: true,
  // On in the sample so PF Members can show the Opt-out drawer (P6b).
  opt_out_allowed: true,
  challan_due_day: "15",
  rounding: "NEAREST",
};

// Not used by the Configuration tab any more (it loads versions from the
// API, see pfConfigApi.js); PF Members still reads its settings from here.
export const sampleVersions = () => [
  {
    id: 1,
    name: "Version 1",
    effective_from: "2024-04-01",
    effective_to: "2026-09-30",
    status: "Superseded",
    description: "PF set up for ABC Manufacturing Ltd. Employer pays 12%, wage ceiling ₹15,000.",
    ...settings,
    wage_ceiling: "15000",
    eps_cap: "15000",
    components: defaultComponents(),
    used_by: ["Apr 2024 – Sep 2026 (30 payrolls)"],
    created_by: "Sadheesh",
    created_at: "2024-03-20T10:30:00",
  },
  {
    id: 2,
    name: "Version 2",
    effective_from: "2026-10-01",
    effective_to: "",
    status: "Active",
    description: "PF wage ceiling ₹25,000; employer contributes 13% (1% above employee) as per company policy from October 2026.",
    ...settings,
    // Employer pays 13%: the rate is written in the ER_EPF formula.
    components: defaultComponents().map((component) =>
      component.code === "ER_EPF" ? { ...component, formula: "ROUND(EPF_WAGE * 13%) - ER_EPS" } : component,
    ),
    used_by: [],
    created_by: "Priya R (HR Manager)",
    created_at: "2026-09-18T15:05:00",
  },
];

export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");
export const formatMonth = (value) => (value ? moment(value).format("MMM YYYY") : "–");
export const versionRange = (version) =>
  `${formatDay(version.effective_from)} → ${version.effective_to ? formatDay(version.effective_to) : "onwards"}`;

// The active saved version (a draft is never the active one).
export const activeVersion = (versions) => {
  const saved = versions.filter((version) => version.status !== "Draft");
  return saved.find((version) => version.status === "Active") || saved[saved.length - 1] || null;
};

// A version used by a finalised payroll can't be changed (B3.2); the
// server's is_locked wins.
export const isLocked = (version) =>
  Boolean(version.is_locked) || version.status === "Superseded" || (version.used_by || []).length > 0;

// Create New Version: the active version copied as a Draft from the next
// 1st of a month after it starts. The draft stays in the browser until
// Save & Activate (id "draft" never clashes with a server id).
export const draftFrom = (versions) => {
  const base = activeVersion(versions);
  const savedCount = versions.filter((version) => version.status !== "Draft").length;
  const after = moment.max(moment().add(1, "month").startOf("month"), moment(base.effective_from).add(1, "month").startOf("month"));
  return {
    ...JSON.parse(JSON.stringify(base)),
    id: "draft",
    source_version_id: base.id,
    name: `Version ${savedCount + 1}`,
    effective_from: after.format("YYYY-MM-DD"),
    effective_to: "",
    status: "Draft",
    description: "",
    is_locked: false,
    used_by: [],
    row_version: null,
    created_by: "",
    created_at: "",
    updated_by: "",
    updated_at: "",
  };
};

// ---- Validation (B3.1 table, AC-C01 / C02) ----

const num = (value) => (value === "" || value == null ? NaN : Number(value));

export const validateSettings = (form, versions) => {
  const errors = {};
  const need = (key, label) => {
    if (form[key] === "" || form[key] == null) errors[key] = `Enter ${label}`;
  };
  const name = String(form.name || "").trim();
  if (!name) errors.name = "Enter a version name";
  else if (versions.some((version) => version.id !== form.id && String(version.name || "").trim().toLowerCase() === name.toLowerCase())) {
    errors.name = "A version with this name already exists";
  }
  if (!form.effective_from) errors.effective_from = "Pick the month it starts from";
  else if (moment(form.effective_from).date() !== 1) errors.effective_from = "Must be the 1st of a month";
  // A past month is allowed (spec B2 / B3.1 / AC-C02: 1st of a month, no
  // overlap; B6: applies to payrolls not yet finalised). Past months are
  // protected by locking a version once a payroll uses it (AC-C03).
  else {
    const overlap = versions.some(
      (version) =>
        version.id !== form.id &&
        version.status !== "Draft" &&
        !moment(form.effective_from).isAfter(moment(version.effective_from)),
    );
    if (overlap && form.status === "Draft") errors.effective_from = "Must start after the current version's start month";
  }
  [["wage_ceiling", "the PF wage ceiling"], ["eps_cap", "the EPS wage cap"]].forEach(([key, label]) => {
    need(key, label);
    if (!errors[key] && !(num(form[key]) > 0)) errors[key] = "Must be more than 0";
  });
  need("eps_stop_age", "the age");
  if (!errors.eps_stop_age && !(Number.isInteger(num(form.eps_stop_age)) && num(form.eps_stop_age) >= 1 && num(form.eps_stop_age) <= 99)) {
    errors.eps_stop_age = "Age 1 to 99";
  }
  if (!form.pf_basis_default) errors.pf_basis_default = "Pick the PF basis";
  need("challan_due_day", "the due day");
  if (!errors.challan_due_day && !(Number.isInteger(num(form.challan_due_day)) && num(form.challan_due_day) >= 1 && num(form.challan_due_day) <= 28)) {
    errors.challan_due_day = "Day 1 to 28";
  }
  if (form.vpf_enabled && !(num(form.vpf_max_pct) > 0 && num(form.vpf_max_pct) <= 100)) errors.vpf_max_pct = "1 to 100";

  const active = form.components.filter((component) => component.status === "Active").map((component) => component.code);
  const missing = [...REQUIRED_CALCULATION_CODES, ...REQUIRED_CONTRIBUTION_CODES].filter((code) => !active.includes(code));
  if (missing.length) errors.components = `Required components must be active: ${missing.join(", ")}`;
  return errors;
};

// ---- Formulas ----

// Codes a component's formula may use: the company's salary component
// codes, payroll amounts, settings and the other components. Rates are
// written in the formula itself (e.g. 12%) (contribution components can
// read calculation ones and other contribution ones). These are the
// editor's chips; formulaCodesFor also accepts the legacy _E codes.
export const formulaChipCodes = (components, ownCode, kind, salaryCodes = []) => [
  ...new Set([
    ...salaryCodes,
    ...PAYROLL_CODES.filter((code) => !LEGACY_CODES.includes(code)),
    ...SETTING_CODES,
    ...components
      .filter((component) => component.code !== ownCode && (kind === "CONTRIBUTION" || component.kind === "CALCULATION"))
      .map((component) => component.code),
  ]),
];

export const formulaCodesFor = (components, ownCode, kind, salaryCodes = []) => [
  ...formulaChipCodes(components, ownCode, kind, salaryCodes),
  ...LEGACY_CODES,
];

// The codes each formula reads, for the circular check.
const forReader = (formula) => String(formula || "").replace(/ROUNDUP\(/gi, "CEIL(").replace(/ROUNDDOWN\(/gi, "FLOOR(");

const readsOf = (formula) => {
  const check = checkFormulaSyntax(forReader(formula));
  return check.valid ? check.codes.map((code) => code.toUpperCase()) : [];
};

const findCycle = (components, startCode) => {
  const byCode = Object.fromEntries(components.map((component) => [component.code, component]));
  const seen = new Set();
  const walk = (code, path) => {
    if (path.includes(code)) return [...path, code];
    if (seen.has(code) || !byCode[code]) return null;
    seen.add(code);
    for (const next of readsOf(byCode[code].formula)) {
      const found = walk(next, [...path, code]);
      if (found) return found;
    }
    return null;
  };
  return walk(startCode, []);
};

// { valid, message } for the formula editor (AC-C06: unknown codes, bad
// syntax and circular references are refused).
export const checkPfFormula = (formula, components, own, salaryCodes = []) => {
  const text = String(formula || "").trim();
  if (!text) return { valid: false, message: "Enter a formula" };
  if (text.length > 500) return { valid: false, message: "500 characters at most" };
  const check = checkFormulaSyntax(forReader(text));
  if (!check.valid) return { valid: false, message: check.message || "Check the formula" };
  const allowed = new Set(formulaCodesFor(components, own.code, own.kind, salaryCodes).map((code) => code.toUpperCase()));
  const unknown = check.codes.map((code) => code.toUpperCase()).filter((code) => !allowed.has(code));
  if (unknown.length) return { valid: false, message: `Unknown code: ${unknown.join(", ")}` };
  const withThis = [...components.filter((component) => component.code !== own.code), { ...own, formula: text }];
  const cycle = findCycle(withThis, own.code);
  if (cycle) return { valid: false, message: `Circular reference: ${cycle.join(" → ")}` };
  return { valid: true, message: "Formula is valid" };
};

// Sample employees for "Test on employee" (C2 worked examples).
export const TEST_EMPLOYEES = [
  { id: "E001", name: "Ravi Kumar", BASIC_E: 22500, DA_E: 7500, RETAINING_E: 0, TOTAL_EARNINGS: 42000, VPF_AMOUNT: 1000, EPS_MEMBER: 1, AGE: 36, basis: "ACTUAL" },
  { id: "E002", name: "Anjali Sharma", BASIC_E: 9000, DA_E: 2250, RETAINING_E: 0, TOTAL_EARNINGS: 22500, VPF_AMOUNT: 0, EPS_MEMBER: 1, AGE: 31 },
  { id: "E003", name: "Suresh B", BASIC_E: 7800, DA_E: 2000, RETAINING_E: 0, TOTAL_EARNINGS: 16000, VPF_AMOUNT: 0, EPS_MEMBER: 1, AGE: 58 },
  { id: "E004", name: "Priya N", BASIC_E: 5000, DA_E: 1250, RETAINING_E: 0, TOTAL_EARNINGS: 12500, VPF_AMOUNT: 0, EPS_MEMBER: 1, AGE: 27 },
  { id: "E117", name: "Imran S", BASIC_E: 22000, DA_E: 5500, RETAINING_E: 0, TOTAL_EARNINGS: 36000, VPF_AMOUNT: 0, EPS_MEMBER: 0, AGE: 33 },
];

// Works every component out for one sample employee with the version's
// settings, in order (calculation first), rounding half up. A salary code
// takes the sample's earned amount of the same name (BASIC -> BASIC_E),
// else 0.
export const runComponents = (form, employee, override, salaryCodes = []) => {
  const values = {
    ...Object.fromEntries(salaryCodes.map((code) => [code, employee[`${code}_E`] ?? 0])),
    ...employee,
    CEILING: Number(form.wage_ceiling) || 0,
    EPS_CAP: Number(form.eps_cap) || 0,
    EPS_STOP_AGE: Number(form.eps_stop_age) || 0,
    ACTUAL: 1,
    PF_BASIS: (employee.basis || form.pf_basis_default) === "ACTUAL" ? 1 : 0,
  };
  const ordered = [
    ...form.components.filter((component) => component.kind === "CALCULATION"),
    ...form.components.filter((component) => component.kind === "CONTRIBUTION"),
  ].map((component) => (override && component.code === override.code ? override : component));
  ordered.forEach((component) => {
    if (component.status !== "Active") return;
    // ROUND is half up to whole rupees (812.5 -> 813); ROUNDUP / ROUNDDOWN
    // map to the reader's CEIL / FLOOR.
    const text = String(component.formula || "")
      .replace(/ROUNDUP\(/gi, "CEIL(")
      .replace(/ROUNDDOWN\(/gi, "FLOOR(");
    values[component.code] = calculateFormula(text, values);
  });
  return values;
};

// ---- Establishment codes (P2) ----
// The tab loads codes, sites and EPFO offices from the API
// (pfCodesApi.js). sampleCodes() is sample-only: the PF
// reports (pfReportsSampleMembers.js, pfReportsData.js) still use it.

export const sampleCodes = () => [
  {
    id: 1,
    code: "TNCBE0012345000",
    type: "MAIN",
    branch_name: "ABC Manufacturing Ltd — Head Office",
    coverage_from: "2019-04-01",
    epfo_office: "Coimbatore",
    address: "Plot 22, SIPCOT Industrial Park, Perundurai, Erode 638052",
    locations: ["Erode Plant", "Erode Office"],
    portal_login_owner: "R. Karthik · HR Manager",
    status: "Active",
  },
  {
    id: 2,
    code: "TNCBE0012345001",
    type: "EXTENSION",
    branch_name: "Chennai Branch",
    coverage_from: "2021-06-01",
    epfo_office: "Chennai (Ambattur)",
    address: "4th Floor, Olympia Tech Park, Guindy, Chennai 600032",
    locations: ["Chennai Office"],
    portal_login_owner: "",
    status: "Active",
  },
];

export const CODE_TYPE_LABELS = { MAIN: "Main code", EXTENSION: "Extension code" };

// "TNCBE0012345000" -> "…345000" (Main Code column / short labels).
export const shortCode = (code) => (code ? `…${String(code).slice(-6)}` : "—");

// This company's employees at the ticked sites (drawer hint).
export const employeesAt = (siteIds, sites) =>
  sites.filter((site) => siteIds.includes(site.id)).reduce((sum, site) => sum + site.employees, 0);

export const emptyCode = (codes) => ({
  id: null,
  code: "",
  type: codes.some((code) => code.type === "MAIN") ? "EXTENSION" : "MAIN",
  branch_name: "",
  coverage_from: moment().add(1, "month").startOf("month").format("YYYY-MM-DD"),
  epfo_office_id: "",
  address: "",
  site_ids: [],
  portal_login_owner: "",
  status: "Active",
});

// B2 / B3.3 (client copy of the server rules, for instant errors): 15
// characters (5 letters + 10 digits), unique in the company, one main code,
// a site in one active code at a time (checked again when a code is
// switched back on), no deactivating a code with members. Coverage date can
// be any past or future day. Error keys match the server's.
export const validateCode = (form, codes, sites = []) => {
  const errors = {};
  const code = String(form.code || "").trim().toUpperCase();
  if (!code) errors.code = "Enter the code exactly as on EPFO";
  else if (!/^[A-Z]{5}\d{10}$/.test(code)) errors.code = "15 characters: 5 letters then 10 digits, e.g. TNCBE0012345002";
  else if (codes.some((item) => item.id !== form.id && item.code === code)) errors.code = "This code is already added";
  if (form.type === "MAIN" && codes.some((item) => item.id !== form.id && item.type === "MAIN")) errors.type = "A main code is already added";
  if (!String(form.branch_name || "").trim()) errors.branch_name = "Enter the branch name";
  if (!form.coverage_from) errors.coverage_from = "Pick the coverage date";
  const officeTyped = String(form.epfo_office || "").trim();
  if ((form.epfo_office_id === "" || form.epfo_office_id == null) && !officeTyped) errors.epfo_office = "Enter the EPFO regional office";
  else if (officeTyped.length > 150) errors.epfo_office = "Use 150 characters or fewer";
  const siteIds = form.site_ids || [];
  if (!siteIds.length) errors.sites = "Pick at least one site";
  else if (form.status === "Active") {
    const clash = sites.find((site) => siteIds.includes(site.id) && site.linked_code_id != null && site.linked_code_id !== form.id);
    if (clash) errors.sites = `${clash.name} is already in ${clash.linked_code}. A site can be in only one PF establishment at a time.`;
  }
  const saved = codes.find((item) => item.id === form.id);
  if (saved && saved.status === "Active" && form.status !== "Active" && saved.members > 0) {
    errors.status = `${saved.members} members are filed under this code in the open month. Move their sites first.`;
  }
  return errors;
};

// ---- Exit codes (P3) ----
// The tab loads codes, exit reasons and the mapping from the API. These
// three are sample-only, still used by the PF reports (pfReportsData.js).

export const EPFO_EXIT_CODES = [
  { code: "C", label: "Cessation (short service)" },
  { code: "S", label: "Superannuation" },
  { code: "R", label: "Retirement" },
  { code: "D", label: "Death in service" },
  { code: "P", label: "Permanent disablement" },
];

export const sampleExitMapping = () => ({
  C: ["Resignation", "Termination", "Absconding", "Contract ended"],
  S: ["Superannuation (age 58)"],
  R: ["Voluntary retirement"],
  D: ["Death"],
  P: ["Permanent disablement"],
});

export const codeOfReason = (mapping, reason) => Object.keys(mapping).find((code) => mapping[code].includes(reason)) || null;
